import React, { useEffect } from "react";
import { useState } from "react";
import styled from "styled-components";
import {
  Radio,
  Typography,
  Input,
  Tag,
  Card,
  Banner,
  Icon,
  CircularLoader,
} from "@plum-xoxo/admin-dsm";
import ActionModal from "../../components/Modals/ActionModal";
import { GATEWAY_ID_TO_NAME_MAPPING } from "./../../constants/PaymentConstant";

const CustomMarkerPoints = styled(Typography.HelperText).attrs()`
  ::marker {
    color: ${(props) => props.theme.palette.primary.c02};
    margin-left: 8px;
  }
`;

const StyledCard = styled(Card).attrs()`
  border: 1px solid
    ${(props) =>
      props.checked
        ? props.theme.palette.primary.c02
        : props.theme.palette.light.c300};
`;

const PaymentMethods = ({
  selectedPayment = "payu",
  setPaymentMethod,
  currencyCode = "INR",
  redirectPage = null,
  setRedirectPage,
  kyb_status,
  is_proforma_invoice,
  calculateAmount,
  handleAmountChange,
  addFund,
  pgs,
  pgMap,
  pgsLoading,
}) => {
  const [currentEventValue, setCurrentEventValue] = useState(null);
  useEffect(() => {
    const abortController = new AbortController();
    selectedPayment && calculateAmount(addFund, abortController.signal);
    !redirectPage && handleAmountChange({ target: { value: `${addFund}` } });
  }, [selectedPayment]);
  const noCardCurrencyWithoutKYB =
    currencyCode !== "INR" && currencyCode !== "USD";
  let paymentModes = [];

  const getOnlinePaymentMode = (pg) => {
    return {
      id: 1,
      title: (
        <>
          <Typography.H3
            component="span"
            color={selectedPayment == pg.gatewayId ? "primary" : "dark"}
            shade={selectedPayment == pg.gatewayId ? "c02" : "c300"}
          >
            Pay Online
          </Typography.H3>
          <Tag
            variant="status"
            color="success"
            label="Recommended"
            mr={2}
            className="ml-8"
          />
        </>
      ),
      description: {
        message: (
          <div>
            <Typography.HelperText color="dark" shade="c200" className="mb-0">
              Pay using Netbanking, UPI, Cards, ACH, Wire and others.
            </Typography.HelperText>
          </div>
        ),
      },

      value: pg.gatewayId,
      show: !(kyb_status !== 2 && noCardCurrencyWithoutKYB),
      message: (
        <>
          <div className="d-flex align-items-center mt-12 ml-32">
            {/* <Icon iconName="Confirm" color="green" shade="c300" className="" /> */}
            <Typography.HelperText color="green" shade="c300">
              Instantly credited
            </Typography.HelperText>
          </div>
        </>
      ),
    };
  };
  const getStripeWireMode = (pg) => {
    return {
      id: 4,
      title: (
        <>
          <Typography.H3
            component="span"
            color={selectedPayment === pg.gatewayId ? "primary" : "dark"}
            shade={selectedPayment === pg.gatewayId ? "c02" : "c300"}
          >
            Pay using ACH/Wire
          </Typography.H3>
        </>
      ),
      subTitle: "",
      description: {
        message: (
          <Typography.HelperText color="dark" shade="c200">
            Transfer funds directly from your bank account.
          </Typography.HelperText>
        ),
      },
      value: pg.gatewayId,
      show: true,
      showCancelRewardPopUp: redirectPage && is_proforma_invoice === 0,
      message: (
        <>
          <Typography.HelperText
            color="dark"
            shade="c200"
            className="offline-transfers mt-12"
          >
            <ul className="ml-8">
              <CustomMarkerPoints
                className="mb-8"
                component="li"
                color="dark"
                shade="c200"
              >
                Takes upto 2-3 business days after we receive the payment.
              </CustomMarkerPoints>
              <CustomMarkerPoints component="li" color="dark" shade="c200">
                No additional charges applicable.
              </CustomMarkerPoints>
            </ul>
          </Typography.HelperText>
        </>
      ),
    };
  };

  const getProformaMode = (pg) => {
    return {
      id: 3,
      title: (
        <Typography.H3
          component="span"
          color={selectedPayment === pg.gatewayId ? "primary" : "dark"}
          shade={selectedPayment === pg.gatewayId ? "c02" : "c300"}
        >
          Pay Offline
        </Typography.H3>
      ),
      description: {
        message: (
          <Typography.HelperText color="dark" shade="c200">
            Generate an invoice and pay through your finance team.
          </Typography.HelperText>
        ),
        subInput: <div></div>,
      },
      value: pg.gatewayId,
      show: is_proforma_invoice === 0 ? true : false,
      showCancelRewardPopUp: redirectPage,
      message: (
        <div className="d-flex align-items-center mt-12 ml-32">
          {/* <Icon iconName="Disable" color="orange" shade="c200" className="" /> */}
          <Typography.HelperText color="orange" shade="c200">
            Takes upto 1-2 business days.
          </Typography.HelperText>
        </div>
      ),
    };
  };

  pgs?.map((pg) => {
    if (pg) {
      pgMap[pg.gatewayId] = pg;
      const gatewayName = GATEWAY_ID_TO_NAME_MAPPING[pg.gatewayId];
      let paymentMode;
      if (
        gatewayName == "razorpay" ||
        gatewayName == "stripe" ||
        gatewayName == "payoneer"
      ) {
        paymentMode = getOnlinePaymentMode(pg);
      } else if (gatewayName == "stripe_wire") {
        paymentMode = getStripeWireMode(pg);
      } else if (gatewayName == "proforma" && is_proforma_invoice === 0) {
        paymentMode = getProformaMode(pg);
      } else {
        paymentMode = {
          id: 3,
          title: gatewayName,
          value: pg.gatewayId,
          show: false,
        };
      }
      paymentModes.push(paymentMode);
    }
  });

  const handlePaymentSelect = (paymentMode) => {
    // if (paymentMode === "paypal") {
    //   initiatePaypal();
    // }
    setPaymentMethod(paymentMode);
    // setPaymentGateway(pgMap?.[paymentMode]?.toLowerCase());
  };

  useEffect(() => {
    !selectedPayment &&
      paymentModes?.[0]?.value &&
      handlePaymentSelect(paymentModes?.[0]?.value);
  }, [paymentModes, selectedPayment]);

  return (
    <div className={`paymentMethods ${"mt-4"}`}>
      <Typography.H2 color="dark" shade="c300" className="mb-10">
        Select Payment Method
      </Typography.H2>

      {pgsLoading ? (
        <CircularLoader />
      ) : (
        paymentModes.map((mode, index) => {
          return (
            <StyledCard
              key={mode.id}
              className={`paymentMethod ${"mt-24"}`}
              spaceAround={6}
              shadow={false}
              checked={selectedPayment == mode.value}
            >
              <Radio
                checked={selectedPayment == mode.value}
                onChange={(e) => {
                  mode.showCancelRewardPopUp
                    ? setCurrentEventValue(e.target.value)
                    : handlePaymentSelect(e.target.value);
                }}
                value={mode.value}
                label={mode.title}
                description={mode.subTitle}
                disabled={mode.show === false}
              />
              <div className="ml-2">
                <div className="ml-32">
                  {mode?.description?.message ? mode?.description.message : ""}
                </div>
                <div>{mode.message}</div>
              </div>
              {mode.description ? (
                <>
                  <div className="ml-2 offline_payment mr-36 ml-26">
                    <div className="title inkLight">
                      {mode.description.title}
                    </div>
                    {mode.description.list}
                  </div>
                  {mode?.description?.subInput && mode?.description?.subInput}
                </>
              ) : null}
              {/* {!mode.show && (
              <Banner
                iconName=""
                color="error"
                description="This payment mode is not recommended while sending reward."
                className="py-8"
              />
            )} */}
              {index == 0 && kyb_status !== 2 && (
                <Banner
                  iconName=""
                  description={
                    <Typography.HelperText className="d-flex flex-row">
                      <Typography.LabelText component="span">
                        Note: &nbsp;
                      </Typography.LabelText>
                      {kyb_status !== 2 && noCardCurrencyWithoutKYB
                        ? "Credit/Debit Card "
                        : "Credit card "}{" "}
                      option will be active after business verification.
                    </Typography.HelperText>
                  }
                  className="py-8"
                  color={mode.show ? "info" : "error"}
                />
              )}
            </StyledCard>
          );
        })
      )}

      {currentEventValue && (
        <ActionModal
          header={
            <Icon
              iconName="PlumInfoFill"
              color="orange"
              shade="c200"
              size={48}
            />
          }
          actionModalSubHeader={
            <Typography.H2 color="dark" shade="c300">
              Selecting this option will cancel the current reward.
            </Typography.H2>
          }
          callbackMethod={() => {
            setCurrentEventValue(null);
          }}
          body={
            <Typography component="div" color="dark" shade="c200">
              <ul className="pl-16">
                <CustomMarkerPoints
                  className="mb-16"
                  component="li"
                  color="dark"
                  shade="c200"
                >
                  <Typography component="span">
                    Adding funds using this option may take 2-3 business days.
                    You can wait and resend the reward when funds are added.{" "}
                  </Typography>
                </CustomMarkerPoints>
                <CustomMarkerPoints
                  className="mb-8"
                  component="li"
                  color="dark"
                  shade="c200"
                >
                  <Typography component="span">
                    Alternatively, pay using recommended payment option to send
                    this reward immediately.
                  </Typography>
                </CustomMarkerPoints>
              </ul>
            </Typography>
          }
          buttonText={"Cancel Reward & Add Funds"}
          cancelBtnText={"No, Cancel"}
          actionHandler={() => {
            handlePaymentSelect(currentEventValue);
            setRedirectPage("");
            setCurrentEventValue(null);
          }}
        />
      )}
    </div>
  );
};

export default PaymentMethods;
